import React from "react";
import styled from "styled-components/macro";
import { Button, Card, Icon } from "components/hds";

import useCsvDownload, { CsvDownloadMessages } from "hooks/useCsvDownload";

export interface CsvExportTileProps {
	/** Small uppercase heading — what the report is. */
	label: string;
	/** One sentence: who is in it. */
	description: string;
	/** API path, relative to the base URL. */
	path: string;
	/**
	 * Name to save under when Content-Disposition is not readable cross-origin.
	 * Must produce the same string as `exportFilename()` on the server.
	 */
	fallbackFilename: () => string;
	captions: {
		download: string;
		/** Shown on the button while the request is in flight. */
		preparing: string;
		/** The PII footnote. Every one of these reports carries personal data. */
		note: string;
	};
	/** Omitted, the hook's English defaults are used. */
	messages?: CsvDownloadMessages;
	/**
	 * Extra request headers — `x-org` for an organization-scoped report. The
	 * staff exports on /internal need none.
	 */
	headers?: Record<string, string>;
}

/**
 * One tile that downloads one CSV report.
 *
 * Takes strings rather than translation keys: the same tile carries the
 * staff-only exports on /internal, which is English by design, and the BMS
 * registrations export on /admin/statistics, which is translated. The caller
 * resolves its own wording — see NumberTile, which is shared on the same terms.
 */
const CsvExportTile = ({
	label,
	description,
	path,
	fallbackFilename,
	captions,
	messages,
	headers,
}: CsvExportTileProps) => {
	const { download, downloading, error } = useCsvDownload(
		path,
		fallbackFilename,
		messages,
		headers,
	);

	return (
		<StyledCard>
			<StyledLabel>{label}</StyledLabel>

			<StyledBody>{description}</StyledBody>

			<Button
				action
				icon={<Icon name="DownloadIcon" />}
				onClick={download}
				disabled={downloading}
			>
				{downloading ? captions.preparing : captions.download}
			</Button>

			{error ? <StyledError>{error}</StyledError> : null}

			<StyledNote>{captions.note}</StyledNote>
		</StyledCard>
	);
};

const StyledCard = styled(Card)`
	gap: var(--space-3);
`;

const StyledLabel = styled.span`
	font-size: var(--font-size-caption);
	font-weight: var(--font-weight-bold);
	color: var(--color-text-secondary);
	text-transform: uppercase;
	letter-spacing: 0.04em;
`;

const StyledBody = styled.p`
	margin: 0;
	font-size: var(--font-size-body);
	line-height: 1.5;
	color: var(--color-text);
`;

const StyledNote = styled.p`
	margin: 0;
	font-size: var(--font-size-caption);
	line-height: 1.5;
	color: var(--color-text-secondary);
`;

const StyledError = styled.p`
	margin: 0;
	font-size: var(--font-size-caption);
	color: var(--color-danger);
`;

export default CsvExportTile;
